import {FC, useMemo, useState} from 'react'
import clsx from 'clsx'
import {simpleCurrency} from 'app/format/currency'
import ProductImage from 'app/components/products/ProductImage'
import {KTSVG} from '_metronic/helpers'
import truncate from 'lodash/truncate'
import {sellingPrice} from 'app/features/orders/helper'
import enumerations, {DeliveryPreferences} from 'app/enums/enumerations'
import {ErrorMessage} from '_core'

type Props = {
  orderItem: any
  onUpdateItem: (value: any) => void
  onRemoveItem: (id: string) => void
  isRefundMode?: boolean
  isDisableInput?: boolean
  orderDeliveryMethod: string
  isRefundDiscountValidate?: boolean
  isEditMode?: boolean
}

const ProductListItem: FC<Props> = ({
  orderItem,
  onUpdateItem,
  onRemoveItem,
  isRefundMode,
  isDisableInput,
  orderDeliveryMethod,
  isRefundDiscountValidate,
  isEditMode,
}) => {
  const [inventoryError, setInventoryError] = useState(false)
  const handleChange = (e: any) => {
    const {value} = e.target
    let availableQty = 0
    if (orderDeliveryMethod === DeliveryPreferences.ship) {
      availableQty = orderItem.shipAvailableForAdminPortal
    } else if (orderDeliveryMethod === DeliveryPreferences.pickup) {
      availableQty = orderItem.pickupAvailableForAdminPortal
    } else {
      availableQty = orderItem.shipAvailableForAdminPortal
    }
    if (isEditMode) {
      availableQty += orderItem.originalQty
    }
    if (isEditMode && value > availableQty) {
      setInventoryError(true)
    } else {
      setInventoryError(false)
    }
    if (value <= availableQty) {
      onUpdateItem({...orderItem, variantId: orderItem.variantId, quantity: value})
    }
  }

  const handleDelete = async () => {
    onRemoveItem(orderItem)
  }

  const maxAvailable = useMemo(() => {
    if (orderDeliveryMethod === DeliveryPreferences.ship) {
      return orderItem.shipAvailableForAdminPortal
    } else if (orderDeliveryMethod === DeliveryPreferences.pickup) {
      return orderItem.pickupAvailableForAdminPortal
    }
    return orderItem.available
  }, [
    orderDeliveryMethod,
    orderItem.available,
    orderItem.pickupAvailableForAdminPortal,
    orderItem.shipAvailableForAdminPortal,
  ])

  const showError = useMemo(() => {
    if (
      orderDeliveryMethod === enumerations.DeliveryPreferences.ship &&
      orderItem.quantity > orderItem.shipAvailableForAdminPortal
    ) {
      return true
    } else if (
      orderDeliveryMethod === enumerations.DeliveryPreferences.pickup &&
      orderItem.quantity > orderItem.pickupAvailableForAdminPortal
    ) {
      return true
    } else if (orderItem.quantity <= 0) {
      return true
    }
    return false
  }, [
    orderDeliveryMethod,
    orderItem.pickupAvailableForAdminPortal,
    orderItem.quantity,
    orderItem.shipAvailableForAdminPortal,
  ])

  return (
    <tr>
      <td>
        <div className='d-flex align-items-center'>
          <div className='symbol symbol-50px me-5'>
            <ProductImage product={orderItem.product} />
          </div>
          <div className='d-flex justify-content-start flex-column'>
            <span className='text-dark fw-bolder text-hover-primary mb-1 fs-6'>
              {truncate(orderItem.product.name, {length: 30})}
            </span>
            <span className='text-muted fw-bold text-muted d-block fs-7'>
              {orderItem.variant && orderItem.variant.name
                ? orderItem.variant.name
                : orderItem.variant}
            </span>
          </div>
        </div>
      </td>
      <td>
        <div className='d-flex flex-column'>
          <span
            className={clsx(
              'text-dark fs-7 fw-bolder',
              orderItem.discountPrice > 0 && 'text-decoration-line-through'
            )}
          >
            <div className='d-flex flex-column'>
              {orderItem.originalPrice > 0 && orderItem.price != orderItem.originalPrice && (
                <s className='text-danger fw-bold'>{simpleCurrency(orderItem.originalPrice)}</s>
              )}
              <p>{simpleCurrency(orderItem.price)}</p>
            </div>
          </span>
          {orderItem.discountPrice > 0 && (
            <span className='fw-bolder fs-7 text-gray-800 '>
              {simpleCurrency(orderItem.discountPrice)}
            </span>
          )}
        </div>
      </td>
      <td>
        <div className='custom-number-input'>
          <input
            id='numOrderListChange'
            type='number'
            className={clsx(
              'form-control form-control-sm text-dark w-75',
              !isEditMode && showError && 'is-invalid'
            )}
            onChange={handleChange}
            value={orderItem.quantity}
            max={isEditMode ? maxAvailable + orderItem.originalQty + 1 : maxAvailable}
            min={1}
            disabled={isDisableInput}
          />
          {isEditMode && inventoryError && (
            <ErrorMessage error={`Only ${maxAvailable} items available`} />
          )}
        </div>
      </td>
      <td className='text-end text-dark fs-7 fw-bolder'>
        {simpleCurrency(orderItem.quantity * sellingPrice(orderItem))}
      </td>
      <td>
        <button
          id='btnOrdersListDelete'
          type='button'
          className='btn btn-icon btn-sm'
          onClick={handleDelete}
          disabled={isRefundDiscountValidate}
        >
          {!isRefundMode ? (
            <span className='svg-icon svg-icon-1'>
              <KTSVG
                path='/media/icons/corksy/delete.svg'
                className='svg-icon-2hx svg-icon-light'
              />
            </span>
          ) : (
            <span className='svg-icon svg-icon-1 border p-1 mt-0 bg-white-500 shadow-sm rounded-2'>
              <KTSVG path='/media/icons/corksy/undo.svg' className='svg-icon-1qt svg-icon-gray' />
            </span>
          )}
        </button>
      </td>
    </tr>
  )
}

export default ProductListItem
